/* eslint-disable  */
import React, { useState, useRef, useEffect } from "react";
import {
  Home,
  HandCoins,
  TrendingUp,
  Banknote,
  BookOpen,
  CheckCircle2,
  Receipt,
  ReceiptText,
  Archive,
  Users,
  UserCog,
  BarChart3,
  ChevronDown,
  UserCheck,
  FileText,
  FileCheck2,
  LogOut,
  Menu,
  X,
  Bell,
  MessageSquare,
  ChevronLeft,
  ChevronUp,
  Shield,
  Headset,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ThemeToggle from "../Components/ThemeToggle";
import { SignOut } from "../services/Actions/authActions";
import "./AdminDashboard.css";

const menuItems = [
  { section: "Workspace", label: "Dashboard", icon: Home, path: "/admin/home" },
  { section: "Finance", label: "Funds Deduction", icon: HandCoins, path: "/admin/funds" },
  // { label: "Contribution Analysis", icon: UserCheck, path: "/admin/fund-analysis" },
  { label: "Fund Increment", icon: TrendingUp, path: "/admin/fund-increment" },
  { label: "Statement", icon: ReceiptText, path: "/admin/statement" },
  { label: "Loan Deduction", icon: Banknote, path: "/admin/loans" },
  // { label: "Loan Deduction Analysis", icon: UserCheck, path: "/admin/loan-analysis" },
  { label: "Initiated Loans", icon: BookOpen, path: "/admin/loans-bookings" },
  { label: "Paid Loans", icon: CheckCircle2, path: "/admin/loans-paid" },
  { label: "Dues Deduction", icon: Receipt, path: "/admin/dues" },
  { section: "People", label: "UPNMG Members", icon: Users, path: "/admin/members" },
  { label: "System Users", icon: UserCog, path: "/admin/users" },
  { section: "Insights & services", label: "Archives", icon: Archive, path: "/admin/archives" },
  { label: "Charts", icon: BarChart3, path: "/admin/charts" },
  {
    label: "Clearance",
    icon: FileCheck2,
    children: [
      { label: "Issue Clearance", icon: FileCheck2, path: "/admin/clearance" },
      { label: "Clearance Dashboard", icon: BarChart3, path: "/admin/clearance-dashboard" },
    ],
  },
  { label: "Report", icon: FileText, path: "/admin/report", divider: true },
  // Leaves the admin shell for the Customer Service module; admins are admitted
  // by PrivateCustomerServiceRoute on their ADMIN super_role.
  { label: "Support Desk", icon: Headset, path: "/support/home" },
];

function RenderAdminPage({ children, title }) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user"));
  const [desktopCollapsed, setCollapsed] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 769px)").matches);
  const collapsed = desktopCollapsed && isDesktop;
  const [openSections, setOpenSections] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 769px)");
    const update = () => { setIsDesktop(media.matches); setMobileOpen(false); };
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const close = (event) => { if (event.key === "Escape") { setMobileOpen(false); setProfileMenuOpen(false); } };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, []);

  // Close the profile menu when clicking outside of it
  useEffect(() => {
    if (!profileMenuOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileMenuOpen]);

  const handleLogout = () => {
    dispatch(SignOut());
    setTimeout(() => {
      window.location.reload(false);
    }, 1000);
  };

  const isActive = (path) => location.pathname === path;

  const toggleSection = (label) =>
    setOpenSections((prev) =>
      prev.includes(label) ? prev.filter((s) => s !== label) : [...prev, label]
    );

  // Expand whichever group owns the current route, so landing on a child link
  // directly doesn't leave it collapsed.
  useEffect(() => {
    const active = menuItems.find((item) =>
      item.children?.some((c) => isActive(c.path))
    );
    if (active) {
      setOpenSections((prev) =>
        prev.includes(active.label) ? prev : [...prev, active.label]
      );
    }
  }, [location.pathname]);

  // Get display name: show the full name, dropping a leading title prefix
  // (Mr./Mrs./Ms./Dr./Prof.). Falls back to the email prefix if the stored
  // name looks like a role code (all caps/underscores), then to "Admin".
  const getDisplayName = () => {
    const name = user?.name?.trim();
    if (name && /^[A-Z_]+$/.test(name)) {
      // Name is all caps/underscores (like a role code), use email instead
      if (user?.email) {
        return user.email.split("@")[0];
      }
    }
    if (name) {
      const cleaned = name
        .replace(/^(mr|mrs|ms|miss|dr|prof)\.?\s+/i, "")
        .trim();
      return cleaned || name;
    }
    return user?.email ? user.email.split("@")[0] : "Admin";
  };

  const getDisplayRole = () => {
    let role = user?.super_role || user?.role;
    if (Array.isArray(role)) role = role[0]; // role can be an array
    if (!role || typeof role !== "string") return "Admin";
    return role
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className={`admin-layout ${collapsed ? "sidebar-collapsed" : ""}`}>
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <Shield size={24} className="brand-icon" />
            {!collapsed && <div className="brand-copy"><span className="brand-text">UPNMG</span><span className="brand-caption">Super Admin</span></div>}
          </div>
          <button
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="collapse-btn hidden-mobile"
            onClick={() => setCollapsed(!collapsed)}
          >
            <ChevronLeft
              size={18}
              style={{
                transform: collapsed ? "rotate(180deg)" : "none",
                transition: "transform 0.3s",
              }}
            />
          </button>
          <button
            aria-label="Close navigation" className="collapse-btn show-mobile"
            onClick={() => setMobileOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Administration navigation">
          {menuItems.map((item) => {
            // Collapsible group. When the sidebar is icon-only there's no room
            // for the child list, so the icon goes to the first child instead
            // of toggling a section the user can't see.
            if (item.children) {
              const open = openSections.includes(item.label);
              const childActive = item.children.some((c) => isActive(c.path));
              return (
                <React.Fragment key={item.label}>
                  {item.section && !collapsed && <p className="sidebar-section-label">{item.section}</p>}
                  <button type="button" aria-label={item.label} aria-expanded={!collapsed && open}
                    className={`sidebar-menu-item ${childActive ? "active" : ""}`}
                    onClick={() => {
                      if (collapsed) {
                        history.push(item.children[0].path);
                        setMobileOpen(false);
                      } else {
                        toggleSection(item.label);
                      }
                    }}
                    title={collapsed ? item.label : ""}
                  >
                    <item.icon size={19} className="menu-icon" />
                    {!collapsed && (
                      <>
                        <span className="menu-label">{item.label}</span>
                        <ChevronDown
                          size={14}
                          className="menu-chevron"
                          style={{
                            marginLeft: "auto",
                            transition: "transform .2s",
                            transform: open ? "rotate(180deg)" : "none",
                          }}
                        />
                      </>
                    )}
                  </button>

                  {!collapsed && open && (
                    <div className="sidebar-submenu">
                      {item.children.map((child) => (
                        <button type="button" aria-current={isActive(child.path) ? "page" : undefined}
                          key={child.path}
                          className={`sidebar-menu-item sidebar-submenu-item ${isActive(child.path) ? "active" : ""}`}
                          onClick={() => {
                            history.push(child.path);
                            setMobileOpen(false);
                          }}
                        >
                          <child.icon size={16} className="menu-icon" />
                          <span className="menu-label">{child.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={item.path}>
                {item.section && !collapsed && <p className="sidebar-section-label">{item.section}</p>}
                <button type="button" aria-label={item.label} aria-current={isActive(item.path) ? "page" : undefined}
                  className={`sidebar-menu-item ${isActive(item.path) ? "active" : ""}`}
                  onClick={() => {
                    history.push(item.path);
                    setMobileOpen(false);
                  }}
                  title={collapsed ? item.label : ""}
                >
                  <item.icon size={19} className="menu-icon" />
                  {!collapsed && <span className="menu-label">{item.label}</span>}
                </button>
              </React.Fragment>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-profile" ref={profileRef}>
            {profileMenuOpen && (
              <div className="profile-menu">
                <button type="button"
                  className="profile-menu-item logout-item"
                  onClick={handleLogout}
                >
                  <LogOut size={18} className="menu-icon" />
                  <span>Logout</span>
                </button>
              </div>
            )}
            <button type="button" aria-label="Account menu" aria-expanded={profileMenuOpen}
              className={`sidebar-user ${profileMenuOpen ? "sidebar-user--active" : ""}`}
              onClick={() => setProfileMenuOpen((open) => !open)}
              title={collapsed ? getDisplayName() : ""}
            >
              <div className="user-avatar">
                {user?.profile_image ? (
                  <img
                    src={user.profile_image}
                    alt={getDisplayName()}
                    className="user-avatar-img"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  getDisplayName().charAt(0).toUpperCase()
                )}
              </div>
              {!collapsed && (
                <>
                  <div className="user-info">
                    <div className="user-name">{getDisplayName()}</div>
                    <div className="user-role">{getDisplayRole()}</div>
                  </div>
                  <ChevronUp
                    size={16}
                    className={`profile-chevron ${profileMenuOpen ? "profile-chevron--open" : ""}`}
                  />
                </>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="topbar-left">
            <button aria-label="Open navigation" aria-expanded={mobileOpen} className="mobile-menu-btn" onClick={() => setMobileOpen(true)}>
              <Menu size={22} />
            </button>
            <h1 className="page-title">{title}</h1>
          </div>
          <div className="topbar-right">
            <ThemeToggle />
            <button className="topbar-icon-btn">
              <MessageSquare size={20} />
            </button>
            <button className="topbar-icon-btn">
              <Bell size={20} />
            </button>
          </div>
        </header>

        <div className="admin-content">{children}</div>
      </main>
    </div>
  );
}

export default RenderAdminPage;
